import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, resolveImageUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
  Plus, Trash2, ExternalLink, LogOut, Upload, Lock, CreditCard,
  Palette, Type, MousePointerClick, Megaphone, LayoutGrid, Inbox, BarChart3, Layers,
  Check, AlertCircle, CloudUpload, Send, Sparkles, Clock, Copy, Link2, Mail,
} from "lucide-react";
import { GridEditor } from "@/components/GridEditor";
import { Logo } from "@/components/Logo";
import { ButtonStyleEditor } from "@/components/ButtonStyleEditor";
import { FormsManager } from "@/components/FormsManager";
import { DevicesManager } from "@/components/DevicesManager";
import { AnalyticsPanel } from "@/components/AnalyticsPanel";
import { PcoConnect } from "@/components/PcoConnect";
import { EmailSettings } from "@/components/EmailSettings";
import { ActionFields } from "@/components/ActionFields";
import { KioskPreview } from "@/components/KioskPreview";
import { VIEWPORT_PRESETS } from "@/components/Viewport";
import { toast } from "sonner";

const FONT_FAMILIES = ["Montserrat","Inter","Poppins","Lato","Raleway","Oswald","Playfair Display","Cormorant Garamond","Merriweather","Roboto Slab"];
const WEIGHTS = ["300","400","500","600","700","800","900"];
const SHADOWS = ["none","sm","md","lg","xl"];

const TEMPLATE_SNAPSHOT_KEYS = ["layout_preset","layout_mode","welcome_subtext","bg_color","accent_color","default_font","heading_font","button_style","buttons","featured_events","carousel_mode","logo_position","clock_position","controls_style","controls_position","controls_size","grid_layout"];

// Canonical JSON so draft-vs-published comparison is order-independent.
const stable = (v) => {
  const norm = (x) => {
    if (Array.isArray(x)) return x.map(norm);
    if (x && typeof x === "object") return Object.keys(x).sort().reduce((a, k) => { a[k] = norm(x[k]); return a; }, {});
    return x;
  };
  return JSON.stringify(norm(v ?? {}));
};

function TemplatesPanel({ cfg, setCfg }) {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const load = async () => { try { const { data } = await api.get("/tenant/templates"); setTemplates(data); } catch {} };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    const snapshot = {};
    TEMPLATE_SNAPSHOT_KEYS.forEach((k) => { if (cfg[k] !== undefined) snapshot[k] = cfg[k]; });
    try { await api.post("/tenant/templates", { name: name.trim(), snapshot }); toast.success("Template saved"); setName(""); load(); } catch (e) { toast.error("Save failed"); }
  };
  const apply = (t) => { setCfg({ ...cfg, ...t.snapshot }); toast.success(`Template applied to your draft: ${t.name}`); };
  const del = async (id) => { if (!window.confirm("Delete this template?")) return; try { await api.delete(`/tenant/templates/${id}`); load(); } catch {} };
  return (
    <div className="space-y-3" data-testid="templates-panel">
      <div className="flex gap-2 flex-wrap">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sunday Morning Layout" className="h-9 flex-1 min-w-[200px]" data-testid="template-name" />
        <Button onClick={save} className="bg-[#2A2626] hover:bg-[#1a1717] text-white rounded-full h-9 px-4" data-testid="template-save">Save as template</Button>
        <Button onClick={async () => { try { const { data } = await api.post("/tenant/templates/starter-pack"); toast.success(data.message || `Installed ${data.installed} starter templates`); load(); } catch { toast.error("Install failed"); } }} variant="outline" className="rounded-full h-9 px-4" data-testid="template-starter-pack">✨ Install Starter Pack</Button>
      </div>
      <p className="text-xs text-[#5C5856]">A template is a reusable design snapshot (layout, colors, fonts, buttons, spotlights, controls). Applying it updates the draft of the Experience you're editing — publish or run when ready. It does not create a new Experience or change what's live.</p>
      {templates.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-2 mt-3">
          {templates.map((t) => (
            <Card key={t.template_id} className="p-3 flex items-center justify-between gap-2" data-testid={`template-${t.template_id}`}>
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{t.name}</p>
                <p className="text-[10px] text-[#5C5856]">{new Date(t.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => apply(t)} className="h-8 text-xs rounded-full" data-testid={`template-apply-${t.template_id}`}>Apply</Button>
                <Button size="sm" variant="ghost" onClick={() => del(t.template_id)} className="h-8 w-8 p-0"><Trash2 className="w-3 h-3 text-[#B23A48]" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {templates.length === 0 && <p className="text-xs italic text-[#5C5856]">No saved templates yet.</p>}
    </div>
  );
}

function FontPicker({ value, onChange, label }) {
  return (
    <div className="space-y-2">
      {label && <Label className="text-xs font-medium uppercase tracking-wider text-[#5C5856]">{label}</Label>}
      <div className="grid grid-cols-2 gap-2">
        <Select value={value.family} onValueChange={(v) => onChange({ ...value, family: v })}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f} value={f} style={{ fontFamily: `'${f}', sans-serif` }}>{f}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={value.weight} onValueChange={(v) => onChange({ ...value, weight: v })}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>{WEIGHTS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-[#5C5856]">Size: {value.size}px</Label>
          <Slider min={10} max={80} step={1} value={[value.size]} onValueChange={(v) => onChange({ ...value, size: v[0] })} />
        </div>
        <div>
          <Label className="text-[10px] text-[#5C5856]">Color</Label>
          <Input type="color" value={value.color} onChange={(e) => onChange({ ...value, color: e.target.value })} className="h-9 p-1" />
        </div>
      </div>
    </div>
  );
}

const DESIGN_TABS = [
  { v: "branding", label: "Branding", icon: Palette },
  { v: "typography", label: "Typography", icon: Type },
  { v: "buttons", label: "Buttons", icon: MousePointerClick },
  { v: "spotlight", label: "Spotlight", icon: Megaphone },
  { v: "canvas", label: "Canvas", icon: LayoutGrid },
];

export default function TenantAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [publishedCfg, setPublishedCfg] = useState(null);
  const [forms, setForms] = useState([]);

  // Nav
  const [primary, setPrimary] = useState("design"); // design | connections | experiences
  const [designTab, setDesignTab] = useState("branding");
  const [connTab, setConnTab] = useState("cards"); // cards | insights
  const [notifyEmail, setNotifyEmail] = useState("");
  const [savingNotify, setSavingNotify] = useState(false);
  const [experiences, setExperiences] = useState([]);
  const [activeExpId, setActiveExpId] = useState(null);
  const [editingExpId, setEditingExpId] = useState(null);
  const [defaultLogo, setDefaultLogo] = useState("");
  const editingExpIdRef = useRef(null);
  editingExpIdRef.current = editingExpId;
  const [timezone, setTimezone] = useState("America/Phoenix");
  const [savingTz, setSavingTz] = useState(false);
  const [previewViewport, setPreviewViewport] = useState("ipad_landscape");
  const [designMode, setDesignMode] = useState("edit"); // edit (canvas) | preview (device)
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedCardIdx, setSelectedCardIdx] = useState(null);

  // Draft / publish / autosave state
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [hasUnpublished, setHasUnpublished] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const debounceRef = useRef(null);
  const pendingRef = useRef(false);
  const failedRef = useRef(false);
  const skipRef = useRef(true);
  const loadedRef = useRef(false);
  const publishedStableRef = useRef("");
  const cfgRef = useRef(null);
  cfgRef.current = cfg;

  const loadForms = () => { api.get("/forms").then(({ data }) => setForms(data)).catch(() => {}); };

  const tier = tenant?.tier || tenant?.effective_plan || "connect";
  const isPremium = tier === "grow" || tier === "multiply";
  const trialEnds = tenant?.intro_trial_ends_at;
  const trialDaysLeft = (() => {
    if (!trialEnds) return null;
    const ms = new Date(trialEnds).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  })();
  const onTrial = tenant?.subscription_status === "trialing" && trialDaysLeft !== null && trialDaysLeft > 0;

  const loadExperiences = async (selectId) => {
    const { data } = await api.get("/experiences");
    setExperiences(data.experiences || []);
    setActiveExpId(data.active_experience_id);
    const list = data.experiences || [];
    const target = list.find((e) => e.experience_id === (selectId || editingExpIdRef.current || data.active_experience_id)) || list[0];
    if (!target) return;
    setEditingExpId(target.experience_id);
    setCfg(target.draft_config);
    setPublishedCfg(target.published_config);
    publishedStableRef.current = stable(target.published_config);
    setHasUnpublished(target.has_unpublished_changes);
    skipRef.current = true;
    loadedRef.current = true;
    setSaveStatus("idle");
  };

  useEffect(() => {
    if (user?.role === "super_admin") { navigate("/admin/super", { replace: true }); return; }
    api.get("/tenant/me").then(({ data }) => {
      setTenant(data);
      setNotifyEmail(data.default_notify_email || "");
      setTimezone(data.timezone || "America/Phoenix");
      setDefaultLogo(data.default_logo_url || "");
      loadExperiences();
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    loadForms();
  }, [user, navigate]);

  const doAutosave = async () => {
    const payload = cfgRef.current;
    const expId = editingExpIdRef.current;
    try {
      const { data } = await api.put(`/experiences/${expId}/config`, { config: payload });
      pendingRef.current = false; failedRef.current = false;
      setSaveStatus("saved");
      setHasUnpublished(!!data.has_unpublished_changes);
    } catch (e) {
      pendingRef.current = false; failedRef.current = true;
      setSaveStatus("error");
      toast.error("Couldn't save your draft — check your connection.");
    }
  };

  // Debounced autosave to the DRAFT whenever the config changes.
  useEffect(() => {
    if (!cfg || !loadedRef.current) return;
    if (skipRef.current) { skipRef.current = false; return; }
    pendingRef.current = true;
    failedRef.current = false;
    setSaveStatus("saving");
    setHasUnpublished(stable(cfg) !== publishedStableRef.current);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(doAutosave, 800);
    return () => clearTimeout(debounceRef.current);
  }, [cfg]);

  // Warn on exit ONLY when an autosave is pending or the last autosave failed.
  useEffect(() => {
    const h = (e) => { if (pendingRef.current || failedRef.current) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  const publish = async () => {
    if (pendingRef.current || saveStatus === "saving") { clearTimeout(debounceRef.current); await doAutosave(); }
    if (failedRef.current) { toast.error("Fix the draft save first, then publish."); return; }
    setPublishing(true);
    try {
      const { data } = await api.post(`/experiences/${editingExpIdRef.current}/publish`);
      setPublishedCfg(data.published_config);
      publishedStableRef.current = stable(data.published_config);
      setHasUnpublished(false);
      await loadExperiences(editingExpIdRef.current);
      toast.success("Published — this experience's saved version is updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const createExperience = async () => {
    const name = window.prompt("Name this experience", "New Experience");
    if (name === null) return;
    try {
      const { data } = await api.post("/experiences", { name: name.trim() || "New Experience" });
      await loadExperiences(data.experience_id);
      setPrimary("design");
      toast.success("Experience created — you're now editing it");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create"); }
  };

  const runExperience = async (id) => {
    if (!window.confirm("Make this experience live now? It will publish its current draft and become the active experience on all your kiosks.")) return;
    try {
      await api.post(`/experiences/${id}/run`);
      await loadExperiences(id);
      toast.success("This experience is now live");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't run"); }
  };

  const editExperience = async (id) => { await loadExperiences(id); setPrimary("design"); };

  const renameExperience = async (id, current) => {
    const name = window.prompt("Rename experience", current || "");
    if (name === null || !name.trim()) return;
    try { await api.put(`/experiences/${id}`, { name: name.trim() }); await loadExperiences(); toast.success("Renamed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't rename"); }
  };

  const duplicateExperience = async (id) => {
    try { const { data } = await api.post(`/experiences/${id}/duplicate`); await loadExperiences(); toast.success(`Duplicated as "${data.name}"`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't duplicate"); }
  };

  const deleteExperience = async (id) => {
    if (!window.confirm("Delete this experience? This can't be undone.")) return;
    try { await api.delete(`/experiences/${id}`); await loadExperiences(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete"); }
  };

  const saveDefaultLogo = async (url) => {
    try { await api.put("/tenant/default-logo", { default_logo_url: url }); setDefaultLogo(url); setTenant((t) => ({ ...t, default_logo_url: url })); toast.success("Church default logo updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };

  const uploadImage = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  if (!tenant || !cfg) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#2A2626] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const updateButton = (idx, patch) => {
    const next = [...cfg.buttons]; next[idx] = { ...next[idx], ...patch }; setCfg({ ...cfg, buttons: next });
  };
  const addButton = () => {
    setCfg({ ...cfg, buttons: [...cfg.buttons, { id: `btn_${Date.now()}`, label: "New Button", link_type: "page", link_value: "", page_content: "" }] });
  };
  const removeButton = (idx) => setCfg({ ...cfg, buttons: cfg.buttons.filter((_, i) => i !== idx) });
  const addEvent = () => {
    setCfg({ ...cfg, featured_events: [...cfg.featured_events, { id: `evt_${Date.now()}`, title: "New Event", image_url: "", visible: true, show_text: true, aspect_ratio: "auto", link_type: "page", link_value: "", page_content: "" }] });
  };
  const updateEvent = (idx, patch) => { const next = [...cfg.featured_events]; next[idx] = { ...next[idx], ...patch }; setCfg({ ...cfg, featured_events: next }); };
  const removeEvent = (idx) => setCfg({ ...cfg, featured_events: cfg.featured_events.filter((_, i) => i !== idx) });

  const duplicateCard = (idx) => {
    const src = cfg.buttons[idx];
    const copy = { ...src, id: `btn_${Date.now()}` };
    const next = [...cfg.buttons];
    next.splice(idx + 1, 0, copy);
    setCfg({ ...cfg, buttons: next });
    setSelectedKey(copy.id); setSelectedCardIdx(idx + 1);
    toast.success("Card duplicated");
  };

  const FOCUS_MAP = { logo: "branding", branding: "branding", background: "branding", featured: "spotlight", buttons: "buttons" };
  const handleFocus = (key) => { setPrimary("design"); setDesignTab(FOCUS_MAP[key] || "branding"); };

  // Canvas element selection -> outline + open the matching contextual panel.
  const handleSelect = (key) => {
    setSelectedKey(key);
    if (key === "logo" || key === "brand" || key === "clock") setDesignTab("branding");
    else if (key === "featured") setDesignTab("spotlight");
    else if (key === "background" || key === "canvas") { setDesignTab("canvas"); setSelectedCardIdx(null); }
    else {
      const idx = (cfg.buttons || []).findIndex((b) => b.id === key);
      if (idx !== -1) setSelectedCardIdx(idx);
      setDesignTab("buttons");
    }
  };

  // Any canvas arrangement change commits the visual layout (kept internally as a grid).
  const onGridChange = (g) => setCfg({ ...cfg, grid_layout: g, layout_mode: "grid" });

  const saveNotifyEmail = async () => {
    setSavingNotify(true);
    try {
      const email = notifyEmail.trim();
      await api.put("/tenant/settings", { default_notify_email: email });
      setTenant((t) => ({ ...t, default_notify_email: email }));
      toast.success(email ? "Default notification email saved" : "Default notification email cleared");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally {
      setSavingNotify(false);
    }
  };

  const saveTimezone = async (tz) => {
    setSavingTz(true);
    try {
      await api.put("/tenant/settings", { timezone: tz });
      setTimezone(tz);
      setTenant((t) => ({ ...t, timezone: tz }));
      toast.success("Timezone saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save timezone");
    } finally {
      setSavingTz(false);
    }
  };

  const StatusPill = () => {
    const map = {
      saving: { icon: CloudUpload, text: "Saving…", cls: "text-[#B8934A]" },
      saved: { icon: Check, text: "Saved", cls: "text-[#5F7A5B]" },
      error: { icon: AlertCircle, text: "Save failed", cls: "text-[#B23A48]" },
      idle: { icon: Check, text: "Draft up to date", cls: "text-[#5C5856]" },
    };
    const s = map[saveStatus] || map.idle;
    const Icon = s.icon;
    return (
      <span className={`text-xs font-medium hidden sm:inline-flex items-center gap-1 ${s.cls}`} data-testid="admin-save-status">
        <Icon className="w-3.5 h-3.5" /> {s.text}
      </span>
    );
  };

  const primaryNav = [
    { v: "design", label: "Design", icon: Palette },
    { v: "connections", label: "Connections", icon: Inbox },
    { v: "experiences", label: "Experiences", icon: Layers },
  ];

  const editingExp = experiences.find((e) => e.experience_id === editingExpId);
  const editingName = editingExp?.name || "Experience";
  const isEditingActive = editingExpId && editingExpId === activeExpId;
  const activeName = experiences.find((e) => e.experience_id === activeExpId)?.name || "—";

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Topbar */}
      <header className="bg-white border-b border-[#E5E2DC] px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="admin-logo"><Logo theme="black" height={26} /></Link>
          <span className="text-[#5C5856] text-sm truncate hidden md:inline">/ {tenant.name}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${isPremium ? "bg-[#2A2626] text-white" : "bg-[#F0EBE1] text-[#5C5856]"}`} data-testid="admin-plan-badge">
            {onTrial ? `${tier.toUpperCase()} TRIAL · ${trialDaysLeft}d left` : tier.toUpperCase()}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 inline-flex items-center gap-1 ${hasUnpublished ? "bg-[#FBF0DA] text-[#8A6D2F]" : "bg-[#E7EFE6] text-[#4F6A4C]"}`} data-testid="admin-publish-state" title={`Draft state for the experience you're editing: ${editingName}`}>
            {hasUnpublished ? `Draft · ${editingName}` : `Saved · ${editingName}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill />
          <Link to="/billing" data-testid="admin-billing-link">
            <Button variant="outline" size="sm" className="rounded-full hidden sm:inline-flex"><CreditCard className="w-4 h-4 mr-1" /> {isPremium ? "Manage plan" : "Upgrade"}</Button>
          </Link>
          <Link to={`/kiosk/${tenant.slug}`} target="_blank" data-testid="admin-view-kiosk">
            <Button variant="outline" size="sm" className="rounded-full"><ExternalLink className="w-4 h-4 mr-1" /> View welcome screen</Button>
          </Link>
          <Button
            onClick={publish}
            disabled={publishing || !hasUnpublished}
            title={isEditingActive ? "Publishes your edits to the live kiosk (this experience is active)" : `Saves ${editingName}'s draft as its published version. Does NOT make it live — use Run experience for that.`}
            className={`rounded-full text-white transition-colors ${hasUnpublished ? "bg-[#2A2626] hover:bg-[#1A1717]" : "bg-[#B7B2AA] cursor-default hover:bg-[#B7B2AA]"}`}
            data-testid="admin-publish"
          >
            <Send className="w-4 h-4 mr-1.5" /> {publishing ? "Publishing…" : hasUnpublished ? "Publish changes" : "Published"}
          </Button>
          <Button onClick={() => { logout(); navigate("/"); }} variant="ghost" size="sm" data-testid="admin-logout"><LogOut className="w-4 h-4" /></Button>
        </div>
      </header>

      {/* Primary nav */}
      <div className="bg-white border-b border-[#E5E2DC] px-6 sticky top-[57px] z-20">
        <div className="max-w-7xl mx-auto flex gap-1">
          {primaryNav.map((n) => {
            const Icon = n.icon; const active = primary === n.v;
            return (
              <button key={n.v} onClick={() => setPrimary(n.v)} data-testid={`nav-${n.v}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${active ? "border-[#2A2626] text-[#2A2626]" : "border-transparent text-[#5C5856] hover:text-[#2A2626]"}`}>
                <Icon className="w-4 h-4" /> {n.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* ===================== DESIGN ===================== */}
        {primary === "design" && (
          <div className="space-y-4">
            <Card className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="design-editing-bar">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs uppercase tracking-wider text-[#5C5856] font-medium">Editing</span>
                <Select value={editingExpId || ""} onValueChange={(v) => editExperience(v)}>
                  <SelectTrigger className="h-9 min-w-[200px] font-semibold text-[#2A2626]" data-testid="design-experience-selector"><SelectValue placeholder="Select experience" /></SelectTrigger>
                  <SelectContent>
                    {experiences.map((e) => (
                      <SelectItem key={e.experience_id} value={e.experience_id}>{e.name}{e.experience_id === activeExpId ? " · Live" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isEditingActive
                  ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#E7EFE6] text-[#4F6A4C] font-medium" data-testid="design-editing-active">Active · Live</span>
                  : <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#5C5856] font-medium" data-testid="design-editing-inactive">Not live · Live now: {activeName}</span>}
              </div>
              <div className="flex items-center gap-2">
                {!isEditingActive && editingExpId && (
                  <Button onClick={() => runExperience(editingExpId)} className="bg-[#4F6A4C] hover:bg-[#3f5a3c] text-white rounded-full h-9" data-testid="design-run-experience"><Send className="w-4 h-4 mr-1.5" /> Run experience</Button>
                )}
              </div>
            </Card>
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Controls */}
            <div className="lg:col-span-5 space-y-4">
              <div className="flex flex-wrap gap-1.5">
                {DESIGN_TABS.map((t) => {
                  const Icon = t.icon; const active = designTab === t.v;
                  return (
                    <button key={t.v} onClick={() => setDesignTab(t.v)} data-testid={`design-tab-${t.v}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-[#2A2626] text-white border-[#2A2626]" : "bg-white text-[#5C5856] border-[#E5E2DC] hover:border-[#2A2626]"}`}>
                      <Icon className="w-3.5 h-3.5" /> {t.label}
                    </button>
                  );
                })}
              </div>

              {/* Branding */}
              {designTab === "branding" && (
                <Card className="p-6 space-y-6" data-testid="panel-branding">
                  <div className="rounded-xl bg-[#F7F5F1] border border-[#E5E2DC] p-4 space-y-3">
                    <Label className="text-xs font-medium uppercase tracking-wider text-[#5C5856]">What to show</Label>
                    <div className="flex items-center gap-3">
                      <Switch checked={cfg.show_logo !== false} onCheckedChange={(v) => setCfg({ ...cfg, show_logo: v })} data-testid="admin-toggle-logo" />
                      <Label>Display logo</Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch checked={cfg.show_brand_name !== false} onCheckedChange={(v) => setCfg({ ...cfg, show_brand_name: v })} data-testid="admin-toggle-brand-name" />
                      <Label>Display church name</Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch checked={cfg.show_welcome_subtext !== false} onCheckedChange={(v) => setCfg({ ...cfg, show_welcome_subtext: v })} data-testid="admin-toggle-welcome" />
                      <Label>Display welcome message</Label>
                    </div>
                  </div>
                  <div className={cfg.show_brand_name === false ? "opacity-50" : ""}>
                    <Label>Church name</Label>
                    <Input value={cfg.brand_name} disabled={cfg.show_brand_name === false} onChange={(e) => setCfg({ ...cfg, brand_name: e.target.value })} className="h-10 mt-1" data-testid="admin-brand-name" />
                    {cfg.show_brand_name === false && <p className="text-xs text-[#5C5856] mt-1">Hidden on the welcome screen — value is kept and restored when you turn it back on.</p>}
                  </div>
                  <div className={cfg.show_welcome_subtext === false ? "opacity-50" : ""}>
                    <Label>Welcome subtext</Label>
                    <Input value={cfg.welcome_subtext ?? ""} disabled={cfg.show_welcome_subtext === false} onChange={(e) => setCfg({ ...cfg, welcome_subtext: e.target.value })} placeholder="Welcome — we're so glad you're here." className="h-10 mt-1" data-testid="admin-welcome-subtext" />
                    <p className="text-xs text-[#5C5856] mt-1">{cfg.show_welcome_subtext === false ? "Hidden on the welcome screen — value is kept." : "Shown under your church name on the welcome screen."}</p>
                  </div>
                  <div className={cfg.show_logo === false ? "opacity-50" : ""}>
                    <Label>Logo</Label>
                    <div className="mt-2 inline-flex rounded-full bg-[#F0EBE1] border border-[#E5E2DC] p-1" data-testid="admin-logo-mode">
                      <button type="button" onClick={() => setCfg({ ...cfg, logo_mode: "default" })} className={`px-3 h-8 rounded-full text-xs font-medium ${((cfg.logo_mode || "custom") === "default") ? "bg-[#2A2626] text-white" : "text-[#5C5856]"}`} data-testid="admin-logo-mode-default">Use church default</button>
                      <button type="button" onClick={() => setCfg({ ...cfg, logo_mode: "custom" })} className={`px-3 h-8 rounded-full text-xs font-medium ${((cfg.logo_mode || "custom") === "custom") ? "bg-[#2A2626] text-white" : "text-[#5C5856]"}`} data-testid="admin-logo-mode-custom">Custom for this experience</button>
                    </div>
                    {(cfg.logo_mode || "custom") === "default" ? (
                      <div className="mt-3 flex items-center gap-4">
                        <div className="w-20 h-20 rounded-xl bg-[#F0EBE1] border border-[#E5E2DC] flex items-center justify-center overflow-hidden">
                          {defaultLogo ? <img src={resolveImageUrl(defaultLogo)} alt="Church logo" className="w-full h-full object-contain p-2" data-testid="admin-logo-default-preview" /> : <span className="text-[#5C5856] text-xs">No default</span>}
                        </div>
                        <label className="cursor-pointer" data-testid="admin-default-logo-upload-label">
                          <input type="file" accept="image/*" hidden onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const url = await uploadImage(f); await saveDefaultLogo(url); } catch { toast.error("Upload failed"); } }} data-testid="admin-default-logo-upload" />
                          <span className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#2A2626] text-white text-sm hover:bg-[#1a1717]"><Upload className="w-4 h-4" /> Change church logo</span>
                        </label>
                        <p className="text-[11px] text-[#5C5856] max-w-[180px]">This experience follows your church default logo. Changing it here updates every experience that uses the default.</p>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-center gap-4">
                        <div className="w-20 h-20 rounded-xl bg-[#F0EBE1] border border-[#E5E2DC] flex items-center justify-center overflow-hidden">
                          {cfg.logo_url ? <img src={resolveImageUrl(cfg.logo_url)} alt="Logo" className="w-full h-full object-contain p-2" /> : <span className="text-[#5C5856] text-xs">No logo</span>}
                        </div>
                        <label className={`cursor-pointer ${cfg.show_logo === false ? "pointer-events-none" : ""}`} data-testid="admin-logo-upload-label">
                          <input type="file" accept="image/*" hidden disabled={cfg.show_logo === false} onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const url = await uploadImage(f); setCfg({ ...cfg, logo_url: url }); toast.success("Logo uploaded"); } catch { toast.error("Upload failed"); } }} data-testid="admin-logo-upload" />
                          <span className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#2A2626] text-white text-sm hover:bg-[#1a1717]"><Upload className="w-4 h-4" /> Upload</span>
                        </label>
                        {cfg.logo_url && cfg.show_logo !== false && <Button variant="ghost" size="sm" onClick={() => setCfg({ ...cfg, logo_url: "" })} data-testid="admin-logo-remove">Remove</Button>}
                      </div>
                    )}
                    {cfg.show_logo === false && <p className="text-xs text-[#5C5856] mt-1">Hidden on the welcome screen — your uploaded logo is kept.</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Background color</Label>
                      <Input type="color" value={cfg.bg_color} onChange={(e) => setCfg({ ...cfg, bg_color: e.target.value })} className="h-10 mt-1 p-1" data-testid="admin-bg-color" />
                    </div>
                    <div>
                      <Label>Accent color</Label>
                      <Input type="color" value={cfg.accent_color} onChange={(e) => setCfg({ ...cfg, accent_color: e.target.value })} className="h-10 mt-1 p-1" data-testid="admin-accent-color" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={cfg.show_datetime} onCheckedChange={(v) => setCfg({ ...cfg, show_datetime: v })} data-testid="admin-show-datetime" />
                    <Label>Show date &amp; time</Label>
                  </div>
                  {cfg.show_datetime && (
                    <div>
                      <Label>Clock alignment</Label>
                      <Select value={cfg.clock_position || "right"} onValueChange={(v) => setCfg({ ...cfg, clock_position: v })}>
                        <SelectTrigger className="h-10 mt-1 max-w-[220px]" data-testid="admin-clock-align"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="left">Left</SelectItem>
                          <SelectItem value="center">Center</SelectItem>
                          <SelectItem value="right">Right</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </Card>
              )}

              {/* Typography */}
              {designTab === "typography" && (
                <Card className="p-6 space-y-8" data-testid="panel-typography">
                  <FontPicker label="Heading font (brand name)" value={cfg.heading_font} onChange={(v) => setCfg({ ...cfg, heading_font: v })} />
                  <FontPicker label="Body font (default)" value={cfg.default_font} onChange={(v) => setCfg({ ...cfg, default_font: v })} />
                  <div className="border-t border-[#E5E2DC] pt-6">
                    <Label className="text-xs font-medium uppercase tracking-wider text-[#5C5856] mb-4 block">Default button style</Label>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div><Label className="text-xs">Background</Label><Input type="color" value={cfg.button_style.bg_color} onChange={(e) => setCfg({ ...cfg, button_style: { ...cfg.button_style, bg_color: e.target.value } })} className="h-10 p-1" /></div>
                        <div><Label className="text-xs">Text color</Label><Input type="color" value={cfg.button_style.text_color} onChange={(e) => setCfg({ ...cfg, button_style: { ...cfg.button_style, text_color: e.target.value } })} className="h-10 p-1" /></div>
                        <div><Label className="text-xs">Border radius: {cfg.button_style.border_radius}px</Label><Slider min={0} max={60} step={1} value={[cfg.button_style.border_radius]} onValueChange={(v) => setCfg({ ...cfg, button_style: { ...cfg.button_style, border_radius: v[0] } })} /></div>
                      </div>
                      <div className="space-y-3">
                        <div><Label className="text-xs">Shadow</Label>
                          <Select value={cfg.button_style.shadow} onValueChange={(v) => setCfg({ ...cfg, button_style: { ...cfg.button_style, shadow: v } })}>
                            <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                            <SelectContent>{SHADOWS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Padding Y: {cfg.button_style.padding_y}px</Label><Slider min={8} max={60} step={1} value={[cfg.button_style.padding_y]} onValueChange={(v) => setCfg({ ...cfg, button_style: { ...cfg.button_style, padding_y: v[0] } })} /></div>
                        <div><Label className="text-xs">Padding X: {cfg.button_style.padding_x}px</Label><Slider min={8} max={80} step={1} value={[cfg.button_style.padding_x]} onValueChange={(v) => setCfg({ ...cfg, button_style: { ...cfg.button_style, padding_x: v[0] } })} /></div>
                      </div>
                    </div>
                    <div className="mt-6"><Label className="text-xs">Button font</Label><div className="mt-2"><FontPicker value={cfg.button_style.font} onChange={(v) => setCfg({ ...cfg, button_style: { ...cfg.button_style, font: v } })} /></div></div>
                  </div>
                </Card>
              )}

              {/* Buttons */}
              {designTab === "buttons" && (
                <div className="space-y-4" data-testid="panel-buttons">
                  {cfg.buttons.map((btn, idx) => (
                    <Card key={btn.id} className={`p-5 space-y-4 ${selectedCardIdx === idx ? "ring-2 ring-[#2A2626]" : ""}`} data-testid={`admin-button-${idx}`}>
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold">Card {idx + 1}</h3>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => duplicateCard(idx)} data-testid={`admin-button-duplicate-${idx}`} aria-label="Duplicate card"><Copy className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => removeButton(idx)} data-testid={`admin-button-remove-${idx}`} aria-label="Delete card"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div><Label>Title</Label><Input value={btn.label} onChange={(e) => updateButton(idx, { label: e.target.value })} className="h-10 mt-1" data-testid={`admin-button-label-${idx}`} /></div>
                        <div>
                          <Label>Subtitle <span className="text-[#5C5856] font-normal">(optional)</span></Label>
                          <Input value={btn.subtitle || ""} onChange={(e) => updateButton(idx, { subtitle: e.target.value })} placeholder="A short supporting line shown under the title" className="h-10 mt-1" data-testid={`admin-button-subtitle-${idx}`} />
                        </div>
                      </div>
                      <ActionFields action={btn} onChange={(p) => updateButton(idx, p)} forms={forms} idPrefix={`admin-button-${idx}`} />
                      <div className="border-t border-[#E5E2DC] pt-4 space-y-2">
                        <div className="flex items-center gap-3">
                          <Switch checked={!!btn.qr_enabled} onCheckedChange={(v) => updateButton(idx, { qr_enabled: v })} data-testid={`admin-button-qr-toggle-${idx}`} />
                          <Label className="text-sm">Show QR code button (scan to continue on phone)</Label>
                        </div>
                        {btn.qr_enabled && (
                          <div><Label className="text-xs">QR destination URL (optional — defaults to the button's link)</Label><Input value={btn.qr_url || ""} onChange={(e) => updateButton(idx, { qr_url: e.target.value })} placeholder="https://yourchurch.com/new-here" className="h-9 mt-1" data-testid={`admin-button-qr-url-${idx}`} /></div>
                        )}
                      </div>
                      <div className="border-t border-[#E5E2DC] pt-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <Switch checked={!!btn.style} onCheckedChange={(v) => updateButton(idx, { style: v ? { ...cfg.button_style } : null })} data-testid={`admin-button-style-toggle-${idx}`} />
                          <Label className="text-sm">Custom style for this button (overrides the default)</Label>
                        </div>
                        {btn.style && (<ButtonStyleEditor value={btn.style} onChange={(s) => updateButton(idx, { style: s })} testidPrefix={`admin-button-style-${idx}`} previewLabel={btn.label || "Preview"} />)}
                      </div>
                    </Card>
                  ))}
                  <Button onClick={addButton} variant="outline" className="w-full h-12 rounded-xl border-dashed" data-testid="admin-add-button"><Plus className="w-4 h-4 mr-2" /> Add card</Button>
                </div>
              )}

              {/* Spotlight */}
              {designTab === "spotlight" && (
                <div className="space-y-4" data-testid="panel-spotlight">
                  <div className="rounded-xl bg-[#F0EBE1] border border-[#E5E2DC] px-4 py-3">
                    <p className="text-sm text-[#2A2626] font-medium">Spotlight highlights one thing worth noticing this week.</p>
                    <p className="text-xs text-[#5C5856] mt-0.5">It supports your Next Step buttons — those always stay the main focus.</p>
                  </div>
                  <Card className="p-4 flex items-center gap-3"><Switch checked={cfg.carousel_mode} onCheckedChange={(v) => setCfg({ ...cfg, carousel_mode: v })} data-testid="admin-carousel-toggle" /><Label>Enable carousel (auto-rotates spotlights)</Label></Card>
                  {cfg.featured_events.map((ev, idx) => (
                    <Card key={ev.id} className={`p-5 space-y-4 ${ev.visible === false ? "opacity-70" : ""}`} data-testid={`admin-event-${idx}`}>
                      <div className="flex justify-between items-center gap-2">
                        <h3 className="font-semibold flex items-center gap-2">Spotlight {idx + 1}
                          {ev.visible === false && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#5C5856] border border-[#E5E2DC]">Hidden</span>}
                        </h3>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2" data-testid={`admin-event-visible-wrap-${idx}`}>
                            <Switch checked={ev.visible !== false} onCheckedChange={(v) => updateEvent(idx, { visible: v })} data-testid={`admin-event-visible-${idx}`} />
                            <Label className="text-xs text-[#5C5856]">Show</Label>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeEvent(idx)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Title <span className="text-[#5C5856] font-normal">(optional)</span></Label>
                          <Input value={ev.title} onChange={(e) => updateEvent(idx, { title: e.target.value })} placeholder="Leave blank if the graphic says it all" className="h-10 mt-1" data-testid={`admin-event-title-${idx}`} disabled={ev.show_text === false} />
                        </div>
                        <div>
                          <Label>Image shape</Label>
                          <Select value={ev.aspect_ratio || "auto"} onValueChange={(v) => updateEvent(idx, { aspect_ratio: v })}>
                            <SelectTrigger className="h-10 mt-1" data-testid={`admin-event-aspect-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto">Auto — Scale to fit</SelectItem>
                              <SelectItem value="wide">Wide — 16:9 · 1920×1080</SelectItem>
                              <SelectItem value="square">Square — 1:1 · 1024×1024</SelectItem>
                              <SelectItem value="banner">Banner — 1920×692</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch checked={ev.show_text !== false} onCheckedChange={(v) => updateEvent(idx, { show_text: v })} data-testid={`admin-event-show-text-${idx}`} />
                        <Label className="text-sm">Show title text over the image</Label>
                      </div>
                      <div>
                        <Label>Image</Label>
                        <div className="mt-2 flex items-center gap-4">
                          <div className="w-32 h-20 rounded-xl bg-[#F0EBE1] border border-[#E5E2DC] overflow-hidden">{ev.image_url && <img src={resolveImageUrl(ev.image_url)} alt={ev.title} className="w-full h-full object-cover" />}</div>
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" hidden onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const url = await uploadImage(f); updateEvent(idx, { image_url: url }); toast.success("Uploaded"); } catch { toast.error("Upload failed"); } }} data-testid={`admin-event-upload-${idx}`} />
                            <span className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#2A2626] text-white text-sm"><Upload className="w-4 h-4" /> Upload</span>
                          </label>
                          <p className="text-[11px] text-[#5C5856]">{ev.aspect_ratio === "wide" ? "Recommended 1920×1080 · tile shown 16:9" : ev.aspect_ratio === "square" ? "Recommended 1024×1024 · tile shown 1:1" : ev.aspect_ratio === "banner" ? "Recommended 1920×692 · wide banner tile" : "Any size — scaled to fit, not cropped"}</p>
                        </div>
                      </div>
                      <div className="border-t border-[#E5E2DC] pt-4 space-y-4">
                        <p className="text-xs text-[#5C5856] font-medium">When a guest taps this spotlight</p>
                        <ActionFields action={ev} onChange={(p) => updateEvent(idx, p)} forms={forms} idPrefix={`admin-event-${idx}`} />
                      </div>
                    </Card>
                  ))}
                  <Button onClick={addEvent} variant="outline" className="w-full h-12 rounded-xl border-dashed" data-testid="admin-add-event"><Plus className="w-4 h-4 mr-2" /> Add spotlight</Button>
                </div>
              )}

              {/* Canvas */}
              {designTab === "canvas" && (
                <Card className="p-6 space-y-6" data-testid="panel-canvas">
                  <div>
                    <Label>Arrangement</Label>
                    <Select value={cfg.layout_preset || "split_right"} onValueChange={(v) => setCfg({ ...cfg, layout_preset: v })} disabled={cfg.layout_mode === "grid"}>
                      <SelectTrigger className="h-10 mt-1" data-testid="admin-layout-preset"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="split_right">Spotlight Left · Cards Right</SelectItem>
                        <SelectItem value="split_left">Cards Left · Spotlight Right</SelectItem>
                        <SelectItem value="stacked">Spotlight Top · Cards Below</SelectItem>
                        <SelectItem value="buttons_focus">Cards Only (no spotlight)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#5C5856] mt-2">{cfg.layout_mode === "grid" ? "You've arranged elements freely on the canvas. Reset the arrangement to use a preset again." : "A quick starting arrangement. Drag elements on the canvas for a fully custom layout."}</p>
                    {cfg.layout_mode === "grid" && (
                      <Button variant="outline" size="sm" className="mt-3 rounded-full" onClick={() => setCfg({ ...cfg, grid_layout: null, layout_mode: "preset" })} data-testid="admin-grid-reset">Reset arrangement</Button>
                    )}
                  </div>
                  <div>
                    <Label>Return to welcome after: {cfg.timeout_seconds}s</Label>
                    <Slider min={10} max={600} step={5} value={[cfg.timeout_seconds]} onValueChange={(v) => setCfg({ ...cfg, timeout_seconds: v[0] })} className="mt-3" data-testid="admin-timeout-slider" />
                    <p className="text-xs text-[#5C5856] mt-2">The welcome screen auto-returns after this period of inactivity.</p>
                  </div>
                  <div>
                    <Label>Staff PIN</Label>
                    <Input value={cfg.passcode} onChange={(e) => setCfg({ ...cfg, passcode: e.target.value.replace(/\D/g,"").slice(0,8) })} className="h-10 mt-1 max-w-xs" placeholder="1234" data-testid="admin-passcode" />
                    <p className="text-xs text-[#5C5856] mt-2">Staff enter this to exit the welcome screen (default 1234).</p>
                  </div>
                  <div className="border-t border-[#E5E2DC] pt-6">
                    <Label className="text-xs font-medium uppercase tracking-wider text-[#5C5856] mb-3 block">Home + Settings controls</Label>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Style</Label>
                        <Select value={cfg.controls_style || "slide_out"} onValueChange={(v) => setCfg({ ...cfg, controls_style: v })}>
                          <SelectTrigger className="h-9 mt-1" data-testid="admin-controls-style"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="slide_out">Slide-out tab (subtle)</SelectItem>
                            <SelectItem value="pill">Pill (Home + gear together)</SelectItem>
                            <SelectItem value="stacked">Stacked circles</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Position</Label>
                        <Select value={cfg.controls_position || "bottom-left"} onValueChange={(v) => setCfg({ ...cfg, controls_position: v })}>
                          <SelectTrigger className="h-9 mt-1" data-testid="admin-controls-position"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bottom-right">Bottom Right</SelectItem>
                            <SelectItem value="bottom-left">Bottom Left</SelectItem>
                            <SelectItem value="top-right">Top Right</SelectItem>
                            <SelectItem value="top-left">Top Left</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Size</Label>
                        <Select value={cfg.controls_size || "sm"} onValueChange={(v) => setCfg({ ...cfg, controls_size: v })}>
                          <SelectTrigger className="h-9 mt-1" data-testid="admin-controls-size"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sm">Small (subtle)</SelectItem>
                            <SelectItem value="md">Medium</SelectItem>
                            <SelectItem value="lg">Large</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Canvas + Preview */}
            <div className="lg:col-span-7">
              <div className="lg:sticky lg:top-[125px]">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div className="inline-flex rounded-full bg-[#F0EBE1] p-0.5" data-testid="design-mode-toggle">
                    <button onClick={() => setDesignMode("edit")} data-testid="design-mode-edit" className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${designMode === "edit" ? "bg-white text-[#2A2626] shadow-sm" : "text-[#5C5856]"}`}>Edit</button>
                    <button onClick={() => setDesignMode("preview")} data-testid="design-mode-preview" className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${designMode === "preview" ? "bg-white text-[#2A2626] shadow-sm" : "text-[#5C5856]"}`}>Preview</button>
                  </div>
                  {designMode === "preview" && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#5C5856]">Preview as</span>
                      <Select value={previewViewport} onValueChange={setPreviewViewport}>
                        <SelectTrigger className="h-8 w-[170px]" data-testid="preview-viewport-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {VIEWPORT_PRESETS.map((p) => <SelectItem key={p.v} value={p.v} data-testid={`preview-viewport-${p.v}`}>{p.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                {designMode === "edit" ? (
                  <>
                    <GridEditor cfg={cfg} onChange={onGridChange} selectedKey={selectedKey} onSelect={handleSelect} onDuplicateCard={duplicateCard} onDeleteCard={removeButton} defaultLogo={defaultLogo} />
                    <p className="text-[11px] text-[#5C5856] mt-3 text-center">Click an element to edit it · drag to move · drag the corner to resize · This is your <strong>draft</strong> — press <strong>Publish changes</strong> to go live.</p>
                  </>
                ) : (
                  <>
                    <KioskPreview cfg={cfg} onFocus={handleFocus} viewport={previewViewport} defaultLogo={defaultLogo} />
                    <p className="text-[11px] text-[#5C5856] mt-3 text-center">Accurate device preview of your <strong>draft</strong> — guests see the published version until you press <strong>Publish changes</strong>.</p>
                  </>
                )}
              </div>
            </div>
          </div>
          </div>
        )}

        {/* ===================== CONNECTIONS ===================== */}
        {primary === "connections" && (
          <div className="space-y-5">
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setConnTab("cards")} data-testid="conn-tab-cards" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${connTab === "cards" ? "bg-[#2A2626] text-white border-[#2A2626]" : "bg-white text-[#5C5856] border-[#E5E2DC] hover:border-[#2A2626]"}`}><Inbox className="w-3.5 h-3.5" /> Connect Cards &amp; Responses</button>
              <button onClick={() => setConnTab("insights")} data-testid="conn-tab-insights" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${connTab === "insights" ? "bg-[#2A2626] text-white border-[#2A2626]" : "bg-white text-[#5C5856] border-[#E5E2DC] hover:border-[#2A2626]"}`}><BarChart3 className="w-3.5 h-3.5" /> Insights</button>
              <button onClick={() => setConnTab("integrations")} data-testid="conn-tab-integrations" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${connTab === "integrations" ? "bg-[#2A2626] text-white border-[#2A2626]" : "bg-white text-[#5C5856] border-[#E5E2DC] hover:border-[#2A2626]"}`}><Link2 className="w-3.5 h-3.5" /> Integrations</button>
              <button onClick={() => setConnTab("email")} data-testid="conn-tab-email" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${connTab === "email" ? "bg-[#2A2626] text-white border-[#2A2626]" : "bg-white text-[#5C5856] border-[#E5E2DC] hover:border-[#2A2626]"}`}><Mail className="w-3.5 h-3.5" /> Email</button>
            </div>
            {connTab === "cards" ? (
              <div className="space-y-5">
                <Card className="p-5" data-testid="default-notify-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Send className="w-4 h-4 text-[#2A2626]" />
                    <h3 className="font-semibold text-[#2A2626]">Default notification email</h3>
                  </div>
                  <p className="text-sm text-[#5C5856] mb-3">New Connect Cards send responses here automatically. Cards with their own recipients are left unchanged.</p>
                  <div className="flex gap-2 flex-wrap">
                    <Input type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="office@yourchurch.com" className="h-10 flex-1 min-w-[220px]" data-testid="default-notify-input" />
                    <Button onClick={saveNotifyEmail} disabled={savingNotify} className="bg-[#2A2626] hover:bg-[#1A1717] text-white rounded-full h-10 px-5" data-testid="default-notify-save">{savingNotify ? "Saving…" : "Save"}</Button>
                  </div>
                  <p className="text-[11px] text-[#5C5856] mt-2">Leave blank to disable the default. Each card can still set its own recipients in its settings.</p>
                </Card>
                <Card className="p-5" data-testid="church-timezone-card">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-[#2A2626]" />
                    <h3 className="font-semibold text-[#2A2626]">Church timezone</h3>
                  </div>
                  <p className="text-sm text-[#5C5856] mb-3">Used to show submission times on Planning Center notes in your local time.</p>
                  <Select value={timezone} onValueChange={saveTimezone} disabled={savingTz}>
                    <SelectTrigger className="h-10 max-w-[320px]" data-testid="church-timezone-select">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/New_York">Eastern — America/New_York</SelectItem>
                      <SelectItem value="America/Chicago">Central — America/Chicago</SelectItem>
                      <SelectItem value="America/Denver">Mountain — America/Denver</SelectItem>
                      <SelectItem value="America/Phoenix">Arizona (no DST) — America/Phoenix</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific — America/Los_Angeles</SelectItem>
                      <SelectItem value="America/Anchorage">Alaska — America/Anchorage</SelectItem>
                      <SelectItem value="Pacific/Honolulu">Hawaii — Pacific/Honolulu</SelectItem>
                    </SelectContent>
                  </Select>
                </Card>
                <FormsManager onFormsChanged={loadForms} />
              </div>
            ) : connTab === "insights" ? <AnalyticsPanel /> : connTab === "integrations" ? <PcoConnect /> : <EmailSettings />}
          </div>
        )}

        {/* ===================== EXPERIENCES ===================== */}
        {primary === "experiences" && (
          <div className="space-y-5 max-w-4xl">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-[#2A2626] flex items-center gap-2"><Layers className="w-4 h-4" /> Experiences</h2>
                <p className="text-sm text-[#5C5856] mt-1">Build multiple welcome experiences. Exactly one runs live on your kiosks at a time.</p>
              </div>
              <Button onClick={createExperience} className="bg-[#2A2626] hover:bg-[#1A1717] text-white rounded-full" data-testid="experience-create"><Plus className="w-4 h-4 mr-1.5" /> New experience</Button>
            </div>

            <div className="space-y-3" data-testid="experiences-list">
              {experiences.map((exp) => {
                const isActive = exp.experience_id === activeExpId;
                const isEditing = exp.experience_id === editingExpId;
                return (
                  <Card key={exp.experience_id} className={`p-5 ${isActive ? "border-2 border-[#4F6A4C]" : ""}`} data-testid={`experience-card-${exp.experience_id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-[200px]">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#2A2626]" data-testid={`experience-name-${exp.experience_id}`}>{exp.name}</span>
                          {isActive
                            ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#E7EFE6] text-[#4F6A4C] font-medium" data-testid={`experience-badge-active-${exp.experience_id}`}>Active · Live</span>
                            : <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#5C5856] font-medium" data-testid={`experience-badge-saved-${exp.experience_id}`}>Saved</span>}
                          {isEditing && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBF0DA] text-[#8A6D2F] font-medium">Editing</span>}
                          {exp.has_unpublished_changes && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBF0DA] text-[#8A6D2F]">Unpublished changes</span>}
                        </div>
                        <p className="text-xs text-[#5C5856] mt-1">{exp.published_config?.brand_name} · {(exp.published_config?.layout_mode === "grid") ? "Custom grid" : (exp.published_config?.layout_preset || "split_right").replace(/_/g, " ")}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {!isActive && <Button onClick={() => runExperience(exp.experience_id)} className="bg-[#4F6A4C] hover:bg-[#3f5a3c] text-white rounded-full h-9" data-testid={`experience-run-${exp.experience_id}`}><Send className="w-4 h-4 mr-1.5" /> Run experience</Button>}
                        <Button variant="outline" className="rounded-full h-9" onClick={() => editExperience(exp.experience_id)} data-testid={`experience-edit-${exp.experience_id}`}>Edit</Button>
                        <Button variant="ghost" size="sm" onClick={() => renameExperience(exp.experience_id, exp.name)} data-testid={`experience-rename-${exp.experience_id}`} aria-label="Rename"><Type className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => duplicateExperience(exp.experience_id)} data-testid={`experience-duplicate-${exp.experience_id}`} aria-label="Duplicate"><Copy className="w-4 h-4" /></Button>
                        {!isActive && <Button variant="ghost" size="sm" onClick={() => deleteExperience(exp.experience_id)} data-testid={`experience-delete-${exp.experience_id}`} aria-label="Delete"><Trash2 className="w-4 h-4 text-[#B23A48]" /></Button>}
                      </div>
                    </div>
                    {isEditing && (
                      <div className="mt-4 pt-4 border-t border-[#E5E2DC] flex items-center justify-between gap-3 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${hasUnpublished ? "bg-[#FBF0DA] text-[#8A6D2F]" : "bg-[#E7EFE6] text-[#4F6A4C]"}`}>{hasUnpublished ? "You have unpublished changes" : "Draft matches saved"}</span>
                        <Button onClick={publish} disabled={publishing || !hasUnpublished} className={`rounded-full text-white h-9 ${hasUnpublished ? "bg-[#2A2626] hover:bg-[#1A1717]" : "bg-[#B7B2AA] cursor-default"}`} data-testid="experiences-publish"><Send className="w-4 h-4 mr-1.5" /> {publishing ? "Publishing…" : isActive ? "Publish (updates live)" : "Publish draft"}</Button>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            <Card className="p-6" data-testid="experiences-saved">
              <h2 className="text-base font-semibold text-[#2A2626] flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4" /> Design templates</h2>
              <p className="text-sm text-[#5C5856] mb-4">Reusable design themes you can apply to the experience you're currently editing.</p>
              <TemplatesPanel cfg={cfg} setCfg={setCfg} />
            </Card>

            {isPremium ? (
              <DevicesManager experiences={experiences} slug={tenant?.slug} />
            ) : (
              <Card className="p-5 bg-[#F7F5F1] border-dashed" data-testid="experiences-scheduling-soon">
                <div className="flex items-center gap-3 text-[#5C5856]">
                  <Clock className="w-5 h-5" />
                  <div>
                    <p className="font-medium text-[#2A2626] text-sm">Devices &amp; scheduled experiences</p>
                    <p className="text-xs">Assign experiences to specific kiosks and switch them automatically by day &amp; time — coming with Grow.</p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
